import { Component } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { APP_PROD_VERSION, APP_VERSION } from '../../../../../core/constants/app.constant';
import { SuccessPopupComponent } from '../../../../../core/libraries/popup/components/success-popup/success-popup.component';
import { VerifyEmailPopupComponent } from '../../../../../core/libraries/popup/components/verify-email-popup/verify-email-popup.component';
import { ILoginData } from '../../../../../core/models/auth.model';
import { AuthService } from '../../../../../core/services/api/auth/auth.service';
import { LoaderService } from '../../../../../core/services/loader/loader.service';
import { StorageService } from '../../../../../core/services/storage/storage.service';

@Component({
    selector: 'app-login',
    standalone: false,
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
})
export class LoginComponent {
    protected email = this.route.snapshot.queryParamMap.get('email') || '';
    protected loginForm = this.fb.group({
        password: ['', [Validators.required, Validators.minLength(8)]],
    });
    protected showPassword = false;

    constructor(
        private fb: NonNullableFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private loaderService: LoaderService,
        private authService: AuthService,
        private storageService: StorageService,
        private dialog: MatDialog,
    ) {}

    protected submit(): void {
        if (!this.loginForm.valid || !this.email) {
            return;
        }
        const password = this.loginForm.value.password as string;
        const payload: ILoginData = {
            type: 'email',
            accountId: this.email,
            accountToken: password,
            deviceType: 'web',
            deviceId: this.storageService.getDeviceId() as string,
            appVersion: environment.name === 'PRODUCTION' ? APP_PROD_VERSION : APP_VERSION,
            zoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        this.loaderService.show();
        this.authService
            .login(payload)
            .pipe(
                tap((response) => {
                    if (response.status === 'pending') {
                        const popupConfig = VerifyEmailPopupComponent.getDialogConfig();
                        popupConfig.data = this.email;
                        this.dialog.open(VerifyEmailPopupComponent, popupConfig);
                    } else {
                        this.storageService.setLoginUser(response);
                        this.router.navigate(['/community']);
                    }
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error logging in:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    protected togglePasswordVisibility(): void {
        this.showPassword = !this.showPassword;
    }
}
